"use client";

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, set, Controller } from "react-hook-form"
import * as z from "zod"
import { toast } from "@/components/ui/use-toast"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { buttonVariants } from "@/components/ui/button"
import { Icons } from "@/components/icons"
import { cn } from "@/lib/utils"
import { reviewSchema } from "@/lib/validations/review";
import { Rating, ThinStar } from '@smastrom/react-rating';  
import { useRouter } from "next/navigation";
import { shadeColor } from "@/lib/utils";
import { User } from "@prisma/client";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectGroup,
} from "@/components/ui/select" 
import { usePostHog } from "posthog-js/react";

interface ReviewFormProps extends React.HTMLAttributes<HTMLFormElement> {
    //className?: string;
    userId: string;
    users: Pick<User, "id" | "name">[];
    urlSlug: string | null;
    isLocationProfile: boolean;
    hexColour: string;
    reviewerUserId: string | undefined;
  }  

type FormData = z.infer<typeof reviewSchema>

export function ReviewForm({ className, userId, users, urlSlug, isLocationProfile, hexColour, reviewerUserId, ...props }: ReviewFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
        name: "",
        text: "",
        rating: 5,
        title: "",
        userId: userId,
      },
  })

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [selectedRating, setSelectedRating] = React.useState<number>(5);
  const router = useRouter();
  const posthog = usePostHog();

  const myStyles = {
    itemShapes: ThinStar,
    activeFillColor: hexColour,
    inactiveFillColor: shadeColor(hexColour, 50)
}

  async function onSubmit(data: FormData) {
    setIsSaving(true);
    
    const completeData = { ...data, reviewerUserId: reviewerUserId, origin: 'superstack' };

    const response = await fetch(`/api/reviews/review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(completeData),
    })

    const reviewId = (await response.json()).id;

    setIsSaving(false);
    posthog.capture(
      'Review submitted',
      {
        reviewee_user_id: data.userId,
        reviewee_name: users.find(user => user.id === data.userId)?.name,
        title: data.title,
        text: data.text,
        rating: data.rating,
        name: data.name,
        review_page_username: urlSlug,
        review_id: reviewId
      }
    )
    toast({
        title: "Review submitted, thank you!",
      })
    return router.push(`/${urlSlug}/subscribe`);
  }

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)} {...props}>
      <div className="mt-1 flex items-center justify-center">
        <Rating
            className="mt-1"
            style={{ maxWidth: 250 }}
            onChange={setSelectedRating}
            value={selectedRating}
            halfFillMode='svg'
            itemStyles={myStyles}
        />
        <Input
            id="rating"
            className="mt-1"
            size={32}
            style={{ display: 'none' }}
            value={selectedRating}
            {...register("rating")}
        />
        {errors?.rating && (
            <p className="px-1 text-xs text-red-600">{errors.rating.message}</p>
        )}
      </div>
        <div className="mt-4">
            <Label className="" htmlFor="name">
                Your Name
            </Label>
            <Input
                id="name"
                className="mt-1"
                size={32}
                {...register("name")}
            />
            {errors?.name && (
                <p className="px-1 text-xs text-red-600">{errors.name.message}</p>
            )}
        </div>
        {/* Display none if isLocationProfile is true */}
        <div className={!isLocationProfile ? "hidden" : "mt-4"}>
        {/* Select component for Users */}
        <Label className="" htmlFor="user">
            Review for
        </Label>
        <SelectGroup className="mt-1">
            <Controller
                control={control} 
                name="userId"
                render={({ field }) => (
                    <Select
                        onValueChange={(value) => field.onChange(value)}
                        value={field.value?.toString() || ""}
                    >
                        <SelectTrigger>
                            {field.value ? users.find(user => user.id === field.value)?.name : "Select someone..."}
                        </SelectTrigger>
                        <SelectContent>
                            {users.map(user => (
                                <SelectItem {...register("userId")} key={user.id} value={user.id}>
                                    {user.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            />
        </SelectGroup>
        {errors?.userId && (
          <p>{errors.userId.message}</p>
        )}
      </div>
        <div className="mt-4">
                <Label className="" htmlFor="title">
                    Review Summary
                </Label>
            <Input
                id="title"
                className="mt-1"
                size={32}
                {...register("title")}
                placeholder="Summarise your experience in a few words..."
            />
            {errors?.title && (
                <p className="px-1 text-xs text-red-600">{errors.title.message}</p>
            )}
        </div>
        <div className="mt-4">
            <Label className="" htmlFor="text">
                Review
            </Label>
            <Textarea
                id="text"
                className="mt-1"
                {...register("text")}
            />
            {errors?.text && (
                <p className="px-1 text-xs text-red-600">{errors.text.message}</p>
            )}
        </div>
      <div className="w-full z-10 flex items-center justify-center p-2">
        <button
          type="submit"
          style={{ backgroundColor: hexColour}}
          className={cn(buttonVariants(), className, `my-2 w-full`)}>
          {isSaving && (
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            )}
          <span>Submit</span>
        </button>
      </div>
    </form>
  )
}
